import { BOARD_TOOLS, BrushTool, TEXT_FONTS, TextFont, isPixelTool } from "../../shared/boards";
import { BrushSettings, TOOL_FEEL, brushDefaults } from "./whiteboard_ink";

// what this person carries between boards: brush, colour, each brush's sizes and settings. kept on this device

export type ShapeKind = "line" | "arrow" | "box" | "ellipse";
export const SHAPES: ShapeKind[] = ["line", "arrow", "box", "ellipse"];

export interface Onion {
  on: boolean;
  // frames shown before and after the one being drawn
  before: number;
  after: number;
}

// a brush that draws a line, as opposed to the eraser, the retouch tools and the lasso fill
export function isLineBrush(tool: string): boolean {
  return (BOARD_TOOLS as readonly string[]).includes(tool) && !isPixelTool(tool) && tool !== "fill";
}

export interface Kit {
  tool: BrushTool;
  color: string;
  // size and opacity for every brush, plus the text tool and the stroke eraser
  sizes: Record<string, number>;
  alphas: Record<string, number>;
  settings: Record<string, BrushSettings>;
  recent: string[];
  // once an apple pencil has touched the board, fingers only move it
  penOnly: boolean;
  shape: ShapeKind;
  font: TextFont;
  onion: Onion;
  // the tool each group of the tool strip was on last, so the key shows it and one press brings it back
  picks: Record<string, string>;
  // the lasso picks anything it touches instead of only what it holds
  lassoTouch: boolean;
}

const KIT_KEY = "chix-wb-kit";
const COLOR = /^#[0-9a-f]{6}$/i;

export const SWATCHES = [
  "#1d1c22", "#ffffff", "#8a8f9c", "#d4283a", "#ff6b4a", "#ffc94d", "#5be3a1", "#2e9e6b",
  "#4de8ff", "#2f6bff", "#a99bff", "#7b3fe4", "#ff7ad9", "#c2185b", "#8b5a2b", "#f2d0b5"
];

export function freshKit(): Kit {
  const settings: Record<string, BrushSettings> = {};
  BOARD_TOOLS.forEach((tool) => { settings[tool] = brushDefaults(tool); });
  return {
    tool: "pen",
    color: "#1d1c22",
    sizes: { ...Object.fromEntries(BOARD_TOOLS.map((tool) => [tool, TOOL_FEEL[tool].size])), text: 28, strokeEraser: 12 },
    alphas: { ...Object.fromEntries(BOARD_TOOLS.map((tool) => [tool, TOOL_FEEL[tool].opacity])), text: 1, strokeEraser: 1 },
    settings,
    recent: [],
    penOnly: false,
    shape: "line",
    font: "sans",
    onion: { on: true, before: 1, after: 1 },
    picks: {},
    lassoTouch: false
  };
}

export function loadKit(): Kit {
  const base = freshKit();
  try {
    const saved = JSON.parse(localStorage.getItem(KIT_KEY) || "{}") as Partial<Kit>;
    const settings = { ...base.settings };
    for (const tool of BOARD_TOOLS) {
      const given = saved.settings?.[tool];
      if (given) settings[tool] = { ...settings[tool], ...given };
    }
    const onion = saved.onion || base.onion;
    return {
      tool: isLineBrush(String(saved.tool)) ? saved.tool as BrushTool : base.tool,
      color: COLOR.test(String(saved.color)) ? String(saved.color) : base.color,
      sizes: { ...base.sizes, ...(saved.sizes || {}) },
      alphas: { ...base.alphas, ...(saved.alphas || {}) },
      settings,
      recent: Array.isArray(saved.recent) ? saved.recent.filter((color) => COLOR.test(color)).slice(0, 10) : [],
      penOnly: Boolean(saved.penOnly),
      shape: SHAPES.includes(saved.shape as ShapeKind) ? saved.shape as ShapeKind : base.shape,
      font: TEXT_FONTS.includes(saved.font as TextFont) ? saved.font as TextFont : base.font,
      picks: Object.fromEntries(Object.entries(saved.picks || {}).filter(([key, value]) => typeof key === "string" && typeof value === "string").slice(0, 20)),
      lassoTouch: Boolean(saved.lassoTouch),
      onion: { on: onion.on !== false, before: Math.min(3, Math.max(0, Math.round(Number(onion.before) || 0))), after: Math.min(3, Math.max(0, Math.round(Number(onion.after) || 0))) }
    };
  } catch {
    return base;
  }
}

export function saveKit(kit: Kit): void {
  try {
    localStorage.setItem(KIT_KEY, JSON.stringify(kit));
  } catch {
    // private browsing, the kit just resets next time
  }
}
